import React, { useEffect, useRef, useState } from 'react';
import { Close, Add, Delete } from "@mui/icons-material";
import api from '../../services/api';
import { formatarDiaBR, vesperaDe } from '../Pescadores/InterrupcaoForm';

interface RegistroFormProps {
  registroUid?: string | null;
  pescadorFixo?: { uid_pesca_pescador: string; nome: string } | null;
  onClose: () => void;
  onSaved: () => void;
}

const AMBIENTES = [
  { value: 'fluvial', label: 'Fluvial' },
  { value: 'lacustre', label: 'Lacustre' },
  { value: 'estuário', label: 'Estuário' },
  { value: 'outro', label: 'Outro' },
];

const APRESENTACOES = [
  { value: 'bruto', label: 'Bruto' },
  { value: 'eviscerado', label: 'Eviscerado' },
  { value: 'filé', label: 'Filé' },
];

let seq = 0;
const novoItem = () => ({ _key: `novo-${seq++}`, uid_pesca_especie: '', peso_kg: '', quantidade: '', apresentacao: 'bruto' });

const RegistroForm: React.FC<RegistroFormProps> = ({ registroUid, pescadorFixo, onClose, onSaved }) => {
  const isEdicao = !!registroUid;

  const [uidPescador, setUidPescador] = useState(pescadorFixo?.uid_pesca_pescador || '');
  const [nomePescadorTexto, setNomePescadorTexto] = useState(pescadorFixo?.nome || '');
  const [sugestoesPescador, setSugestoesPescador] = useState<any[]>([]);
  const pescadorDebounce = useRef<any>(null);

  const [uidProjeto, setUidProjeto] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [descricaoLocal, setDescricaoLocal] = useState('');
  const [ambiente, setAmbiente] = useState('');
  const [numPescadores, setNumPescadores] = useState('1');
  const [numEmbarcacoes, setNumEmbarcacoes] = useState('0');
  const [observacao, setObservacao] = useState('');

  const [itens, setItens] = useState<any[]>([novoItem()]);
  const [apetrechosDisponiveis, setApetrechosDisponiveis] = useState<any[]>([]);
  const [apetrechosSelecionados, setApetrechosSelecionados] = useState<string[]>([]);

  const [especies, setEspecies] = useState<any[]>([]);
  const [especiesOrfas, setEspeciesOrfas] = useState<any[]>([]);
  const [projetos, setProjetos] = useState<any[]>([]);

  const [carregando, setCarregando] = useState(isEdicao);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/pesca/projetos').then(res => setProjetos(res.data.data));
    api.get('/pesca/apetrechos').then(res => setApetrechosDisponiveis(res.data.data));
  }, []);

  // Pescador com período sem atividade EM ANDAMENTO: ao criar um registro de pesca, avisa e
  // oferece encerrar o período na véspera da data do registro. Só na criação.
  const [periodosAbertos, setPeriodosAbertos] = useState<any[]>([]);
  const [encerrandoPeriodo, setEncerrandoPeriodo] = useState(false);
  const [erroEncerrar, setErroEncerrar] = useState('');

  const carregarPeriodosAbertos = (uidP: string) => {
    api.get(`/pesca/pescadores/${uidP}/interrupcoes`, { params: { em_aberto: 1 } })
      .then(res => setPeriodosAbertos(res.data.data || []))
      .catch(() => setPeriodosAbertos([]));
  };

  useEffect(() => {
    setErroEncerrar('');
    if (isEdicao || !uidPescador) { setPeriodosAbertos([]); return; }
    carregarPeriodosAbertos(uidPescador);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uidPescador, isEdicao]);

  const encerrarPeriodo = async (periodo: any, dataFim: string) => {
    setEncerrandoPeriodo(true);
    setErroEncerrar('');
    try {
      await api.post(`/pesca/interrupcoes/${periodo.uid_pesca_interrupcao_atividade}/encerrar`, { data_fim: dataFim });
      carregarPeriodosAbertos(uidPescador);
    } catch (err: any) {
      setErroEncerrar(err?.response?.data?.message || 'Não foi possível encerrar o período.');
    } finally {
      setEncerrandoPeriodo(false);
    }
  };

  // Catálogo de espécies contextual ao projeto (SYNCORP-912, Task 6) —
  // prioriza/reduz as opções às espécies habilitadas nesse projeto, com
  // fallback pro catálogo global quando o projeto ainda não tem catálogo
  // configurado (mesma regra do WhatsApp/LIDA e da ficha de campo). Refaz a
  // busca sempre que o projeto muda — inclusive pra "" (catálogo global).
  useEffect(() => {
    const params = new URLSearchParams();
    if (uidProjeto) params.set('uid_pesca_projeto', uidProjeto);
    api.get(`/pesca/especies/catalogo?${params.toString()}`).then(res => setEspecies(res.data.data));
  }, [uidProjeto]);

  useEffect(() => {
    if (!registroUid) return;
    api.get(`/pesca/registros/view/${registroUid}`).then(res => {
      const r = res.data.data;
      setUidPescador(r.uid_pesca_pescador || '');
      setNomePescadorTexto(r.pescador?.nome || '');
      setUidProjeto(r.uid_pesca_projeto || '');
      setDataInicio(r.data_inicio ? String(r.data_inicio).slice(0, 10) : '');
      setDataFim(r.data_fim ? String(r.data_fim).slice(0, 10) : '');
      setDescricaoLocal(r.descricao_local || '');
      setAmbiente(r.ambiente || '');
      setNumPescadores(String(r.num_pescadores ?? '1'));
      setNumEmbarcacoes(String(r.num_embarcacoes ?? '0'));
      setObservacao(r.observacao || '');
      setItens(
        (r.itens || []).length
          ? r.itens.map((i: any) => ({
              _key: i.uid_pesca_captura_item,
              uid_pesca_especie: i.uid_pesca_especie,
              peso_kg: i.peso_kg,
              quantidade: i.quantidade ?? '',
              apresentacao: i.apresentacao ?? '',
            }))
          : [novoItem()]
      );
      // Um registro antigo pode ter uma captura de espécie inativa ou fora
      // do catálogo curado do projeto atual (SYNCORP-912, Task 6) — o
      // catálogo contextual não traria essa espécie, e o dropdown apareceria
      // em branco mesmo com o registro tendo um uid_pesca_especie válido.
      // Guardadas à parte (não misturadas em `especies`, que é sobrescrito
      // toda vez que o catálogo contextual é refeito) e combinadas só na
      // hora de montar as opções do dropdown.
      setEspeciesOrfas(
        (r.itens || [])
          .filter((i: any) => i.uid_pesca_especie && i.especie?.nome_popular)
          .map((i: any) => ({ uid_pesca_especie: i.uid_pesca_especie, nome_popular: `${i.especie.nome_popular} (fora do catálogo atual)` }))
      );
      setApetrechosSelecionados(
        (r.apetrechos || []).map((a: any) => a.uid_pesca_apetrecho).filter(Boolean)
      );
      setCarregando(false);
    });
  }, [registroUid]);

  const handleBuscarPescador = (texto: string) => {
    setNomePescadorTexto(texto);
    setUidPescador('');
    if (pescadorDebounce.current) clearTimeout(pescadorDebounce.current);
    if (texto.trim().length < 2) {
      setSugestoesPescador([]);
      return;
    }
    pescadorDebounce.current = setTimeout(async () => {
      const res = await api.get(`/pesca/pescadores?search=${encodeURIComponent(texto.trim())}&limit=10`);
      setSugestoesPescador(res.data.data);
    }, 400);
  };

  const selecionarPescador = (p: any) => {
    setUidPescador(p.uid_pesca_pescador);
    setNomePescadorTexto(p.nome);
    setSugestoesPescador([]);
    prefillFromPescador(p.uid_pesca_pescador);
  };

  // Preenche projeto (só se houver exatamente 1 vínculo) e os demais campos
  // (exceto data e espécies capturadas) a partir do registro mais recente do pescador.
  const prefillFromPescador = async (uidP: string) => {
    if (isEdicao) return; // edição já carrega os próprios dados, não sobrescreve
    try {
      const res = await api.get(`/pesca/pescadores/${uidP}`);
      const p = res.data.data;

      const projetosVinculados = (p.projetoVinculos || []).map((v: any) => v.projeto).filter(Boolean);
      if (projetosVinculados.length === 1) {
        setUidProjeto(projetosVinculados[0].uid_pesca_projeto);
      }

      const registrosOrdenados = (p.registros || []).slice().sort(
        (a: any, b: any) => new Date(b.data_inicio).getTime() - new Date(a.data_inicio).getTime()
      );
      const ultimo = registrosOrdenados[0];
      if (ultimo) {
        setDescricaoLocal(ultimo.descricao_local || '');
        setAmbiente(ultimo.ambiente || '');
        setNumPescadores(String(ultimo.num_pescadores ?? '1'));
        setNumEmbarcacoes(String(ultimo.num_embarcacoes ?? '0'));
        setObservacao(ultimo.observacao || '');
        setApetrechosSelecionados(
          (ultimo.apetrechos || []).map((a: any) => a.apetrecho?.uid_pesca_apetrecho || a.uid_pesca_apetrecho).filter(Boolean)
        );
      }
    } catch {
      // silencioso: se der erro no prefill, o formulário continua vazio normalmente
    }
  };

  // Se o pescador já vem travado (aberto pela ficha do Pescador), prefila assim que monta
  useEffect(() => {
    if (pescadorFixo?.uid_pesca_pescador && !isEdicao) {
      prefillFromPescador(pescadorFixo.uid_pesca_pescador);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atualizarItem = (key: string, campo: string, valor: string) => {
    setItens(prev => prev.map(i => i._key === key ? { ...i, [campo]: valor } : i));
  };

  const adicionarItem = () => setItens(prev => [...prev, novoItem()]);
  const removerItem = (key: string) => setItens(prev => prev.length > 1 ? prev.filter(i => i._key !== key) : prev);

  const toggleApetrecho = (uid: string) => {
    setApetrechosSelecionados(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uidPescador) {
      setErro('Selecione um pescador.');
      return;
    }
    if (!uidProjeto) {
      setErro('Selecione um projeto.');
      return;
    }
    if (!dataInicio) {
      setErro('Informe a data.');
      return;
    }

    setSalvando(true);
    setErro('');
    try {
      const payload = {
        uid_pesca_pescador: uidPescador,
        uid_pesca_projeto: uidProjeto,
        data_inicio: dataInicio,
        data_fim: dataFim || null,
        descricao_local: descricaoLocal.trim() || null,
        ambiente: ambiente || null,
        num_pescadores: numPescadores ? Number(numPescadores) : null,
        num_embarcacoes: numEmbarcacoes ? Number(numEmbarcacoes) : null,
        observacao: observacao.trim() || null,
        itens: itens
          .filter(i => i.uid_pesca_especie && (i.peso_kg || i.quantidade))
          .map(i => ({
            uid_pesca_especie: i.uid_pesca_especie,
            peso_kg: i.peso_kg || null,
            quantidade: i.quantidade || null,
            apresentacao: i.apresentacao || null
          })),
        uid_pesca_apetrechos: apetrechosSelecionados,
      };

      if (isEdicao) {
        await api.put(`/pesca/registros/${registroUid}`, payload);
      } else {
        await api.post('/pesca/registros', payload);
      }
      onSaved();
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar registro.');
    } finally {
      setSalvando(false);
    }
  };

  // Catálogo contextual + espécies de capturas antigas fora dele (inativas
  // ou fora da curadoria do projeto) — garante que um registro antigo
  // sempre abre e edita mostrando o nome real, nunca um dropdown em branco.
  const especiesParaExibir = [
    ...especies,
    ...especiesOrfas.filter((o) => !especies.some((e) => e.uid_pesca_especie === o.uid_pesca_especie)),
  ];

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)' }}>
            {isEdicao ? 'Editar Registro de Pesca' : 'Novo Registro de Pesca'}
          </h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        {carregando ? (
          <p>Carregando...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, flex: 1, position: 'relative' }}>
                <label style={labelStyle}>Pescador *</label>
                <input
                  style={inputStyle}
                  value={nomePescadorTexto}
                  onChange={(e) => handleBuscarPescador(e.target.value)}
                  placeholder="Digite pra buscar..."
                  disabled={!!pescadorFixo}
                  autoComplete="off"
                />
                {sugestoesPescador.length > 0 && (
                  <div style={suggestionBoxStyle}>
                    {sugestoesPescador.map((p) => (
                      <div key={p.uid_pesca_pescador} style={suggestionItemStyle} onClick={() => selecionarPescador(p)}>
                        {p.nome}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Projeto *</label>
                <select style={inputStyle} value={uidProjeto} onChange={(e) => setUidProjeto(e.target.value)}>
                  <option value="">Selecione...</option>
                  {projetos.map((p) => (
                    <option key={p.uid_pesca_projeto} value={p.uid_pesca_projeto}>{p.nome}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Data *</label>
                <input type="date" style={inputStyle} value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
              </div>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Data Fim</label>
                <input type="date" style={inputStyle} value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
              </div>
            </div>

            {periodosAbertos.map((pa) => {
              // véspera da data do registro; só dá para encerrar se cair a partir do início do período
              const vespera = dataInicio ? vesperaDe(dataInicio) : '';
              const podeEncerrar = !!vespera && vespera >= pa.data_inicio;
              const [, mes, dia] = vespera.split('-');
              return (
                <div key={pa.uid_pesca_interrupcao_atividade} style={avisoPeriodoStyle}>
                  <p style={{ margin: 0 }}>
                    Este pescador está com um período sem atividade em andamento desde {formatarDiaBR(pa.data_inicio)}.
                    {dataInicio && !podeEncerrar && ' A data deste registro não é posterior ao início do período: ajuste o período na ficha do pescador.'}
                  </p>
                  {podeEncerrar && (
                    <button
                      type="button"
                      disabled={encerrandoPeriodo}
                      onClick={() => encerrarPeriodo(pa, vespera)}
                      style={{ ...secondaryBtnStyle, marginTop: '8px', padding: '5px 14px', fontSize: '0.85rem' }}
                    >
                      {encerrandoPeriodo ? 'Encerrando...' : `Encerrar período em ${dia}/${mes}`}
                    </button>
                  )}
                  {erroEncerrar && <p style={{ margin: '6px 0 0', color: '#c4283a' }}>{erroEncerrar}</p>}
                </div>
              );
            })}

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, flex: 2 }}>
                <label style={labelStyle}>Local</label>
                <input style={inputStyle} value={descricaoLocal} onChange={(e) => setDescricaoLocal(e.target.value)} placeholder="Ex: Rio Tietê" />
              </div>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Ambiente</label>
                <select style={inputStyle} value={ambiente} onChange={(e) => setAmbiente(e.target.value)}>
                  <option value="">-</option>
                  {AMBIENTES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Nº Pescadores</label>
                <input type="number" min="0" style={inputStyle} value={numPescadores} onChange={(e) => setNumPescadores(e.target.value)} />
              </div>
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Nº Embarcações</label>
                <input type="number" min="0" style={inputStyle} value={numEmbarcacoes} onChange={(e) => setNumEmbarcacoes(e.target.value)} />
              </div>
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Observação</label>
              <textarea style={{ ...inputStyle, minHeight: '50px' }} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
            </div>

            {/* ── Espécies capturadas ─────────────────────────────────── */}
            <div style={{ ...fieldWrapStyle, borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '4px' }}>
              <label style={{ ...labelStyle, fontWeight: 'bold', color: '#444' }}>Espécies Capturadas (por peso)</label>
            </div>

            {itens.map((item) => (
              <div key={item._key} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <select
                  style={{ ...inputStyle, flex: 2 }}
                  value={item.uid_pesca_especie}
                  onChange={(e) => atualizarItem(item._key, 'uid_pesca_especie', e.target.value)}
                >
                  <option value="">Espécie...</option>
                  {especiesParaExibir.map((esp) => (
                    <option key={esp.uid_pesca_especie} value={esp.uid_pesca_especie}>{esp.nome_popular}</option>
                  ))}
                </select>
                <input
                  type="number" min="0" step="0.01"
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="Peso (kg)"
                  value={item.peso_kg}
                  onChange={(e) => atualizarItem(item._key, 'peso_kg', e.target.value)}
                />
                <input
                  type="number" min="0" step="1"
                  style={{ ...inputStyle, flex: '0 0 90px' }}
                  placeholder="Qtd (un)"
                  title="Quantidade em unidades — campo legado do WhatsApp, use pra corrigir registros antigos preenchendo o peso em kg ao lado"
                  value={item.quantidade}
                  onChange={(e) => atualizarItem(item._key, 'quantidade', e.target.value)}
                />
                <select
                  style={{ ...inputStyle, flex: 1 }}
                  value={item.apresentacao}
                  onChange={(e) => atualizarItem(item._key, 'apresentacao', e.target.value)}
                >
                  {!item.apresentacao && <option value="">Não informada</option>}
                  {APRESENTACOES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
                <Delete
                  fontSize="small"
                  style={{ color: '#f75a68', cursor: 'pointer', flexShrink: 0 }}
                  onClick={() => removerItem(item._key)}
                />
              </div>
            ))}

            <button
              type="button"
              onClick={adicionarItem}
              style={{ ...secondaryBtnStyle, display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '14px' }}
            >
              <Add fontSize="small" /> Adicionar espécie
            </button>

            {/* ── Artes de pesca ──────────────────────────────────────── */}
            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Artes de Pesca Utilizadas</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                {apetrechosDisponiveis.map((a) => (
                  <label key={a.uid_pesca_apetrecho} style={chipStyle(apetrechosSelecionados.includes(a.uid_pesca_apetrecho))}>
                    <input
                      type="checkbox"
                      style={{ marginRight: '6px' }}
                      checked={apetrechosSelecionados.includes(a.uid_pesca_apetrecho)}
                      onChange={() => toggleApetrecho(a.uid_pesca_apetrecho)}
                    />
                    {a.nome}
                  </label>
                ))}
                {apetrechosDisponiveis.length === 0 && <span style={{ color: '#999', fontSize: '0.85rem' }}>Nenhuma arte cadastrada.</span>}
              </div>
            </div>

            {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
              <button type="submit" disabled={salvando} style={primaryBtnStyle}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, overflowY: 'auto', padding: '20px 0',
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '90%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};
const fieldWrapStyle: React.CSSProperties = { marginBottom: '14px' };
const avisoPeriodoStyle: React.CSSProperties = {
  margin: '0 0 14px', padding: '8px 10px', borderRadius: '8px', fontSize: '0.85rem', background: '#fff6e0', color: '#8a5a00',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};
const chipStyle = (ativo: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', padding: '4px 10px', borderRadius: '16px',
  border: `1px solid ${ativo ? '#03c9fe' : '#ddd'}`, background: ativo ? '#e6f9ff' : '#fff',
  fontSize: '0.85rem', cursor: 'pointer',
});
const suggestionBoxStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
  border: '1px solid #ddd', borderRadius: '8px', marginTop: '2px', zIndex: 10,
  maxHeight: '160px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
};
const suggestionItemStyle: React.CSSProperties = {
  padding: '8px 10px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0',
};

export default RegistroForm;
